class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.designMode = this.dataset.designMode === 'true';
    this.form = this.querySelector('.sticky-atc__form');
    this.variantInput = this.querySelector('[data-sticky-atc-variant]');
    this.quantityInput = this.querySelector('[data-sticky-atc-quantity]');
    this.imageEl = this.querySelector('.sticky-atc__image');
    this.titleEl = this.querySelector('.sticky-atc__title');
    this.headingEl = this.querySelector('.sticky-atc__heading');
    this.priceCurrentEl = this.querySelector('[data-sticky-atc-price-current]');
    this.priceCompareEl = this.querySelector('[data-sticky-atc-price-compare]');
    this.priceCurrentLabelEl = this.querySelector('[data-sticky-atc-price-current-label]');
    this.priceCompareLabelEl = this.querySelector('[data-sticky-atc-price-compare-label]');
    this.submitButton = this.querySelector('[data-sticky-atc-submit]');
    this.notifyButton = this.querySelector('[data-sticky-atc-notify]');
    this.bis = this.querySelector('.sticky-atc__bis');
    this.variants = this.readVariants();

    this.onVariantChange = this.onVariantChange.bind(this);
    this.onQuantityClick = this.onQuantityClick.bind(this);
    this.onQuantityInput = this.onQuantityInput.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onDocumentQuantityChange = this.onDocumentQuantityChange.bind(this);

    this.querySelector('[data-sticky-atc-minus]')?.addEventListener('click', this.onQuantityClick);
    this.querySelector('[data-sticky-atc-plus]')?.addEventListener('click', this.onQuantityClick);
    this.quantityInput?.addEventListener('change', this.onQuantityInput);
    document.addEventListener('variant:change', this.onVariantChange);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('change', this.onDocumentQuantityChange);

    this.observeTrigger();
    this.syncFromProduct();
    this.observeHeight();
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--sticky-atc-height');
    document.removeEventListener('variant:change', this.onVariantChange);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('change', this.onDocumentQuantityChange);
    this.querySelector('[data-sticky-atc-minus]')?.removeEventListener('click', this.onQuantityClick);
    this.querySelector('[data-sticky-atc-plus]')?.removeEventListener('click', this.onQuantityClick);
    this.quantityInput?.removeEventListener('change', this.onQuantityInput);
  }

  readVariants() {
    const script = this.querySelector('[data-sticky-atc-variants]');
    if (!script) return [];
    try {
      const data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return [];
    }
  }

  productInfo() {
    return document.querySelector('product-info');
  }

  observeTrigger() {
    this.intersectionObserver?.disconnect();
    const trigger = document.querySelector('[data-sticky-atc-trigger], .product-form__atc-row');
    this.currentTrigger = trigger;

    if (!trigger) {
      this.setVisible(this.designMode);
      return;
    }

    // Only show once the main ATC has scrolled up past the top: while it is
    // still below the fold (first view on mobile) the bar stays hidden. The
    // top edge sits under the sticky header, so a row hidden behind the
    // header counts as scrolled past.
    const headerHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')) || 0;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const topEdge = entry.rootBounds ? entry.rootBounds.top : headerHeight;
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= topEdge;
        this.setVisible(this.designMode || scrolledPast);
      },
      { threshold: 0, rootMargin: `-${headerHeight}px 0px 0px 0px` }
    );
    this.intersectionObserver.observe(trigger);
  }

  // Publishes the bar's height so the page can reserve that much space at the
  // bottom while the bar is visible (section-sticky-atc.css). Re-measured on
  // resize because the bar changes height between its add-to-cart and notify-me
  // states.
  observeHeight() {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--sticky-atc-height', `${this.getBoundingClientRect().height}px`);
    });
    this.resizeObserver.observe(this);
  }

  setVisible(visible) {
    const isVisible = this.classList.contains('is-visible');
    if (isVisible === visible) return;

    const activeElement = document.activeElement;
    const focusInside = activeElement instanceof Node && this.contains(activeElement);
    const trigger = this.currentTrigger;
    const focusOnTrigger =
      trigger instanceof Element &&
      activeElement instanceof Node &&
      (activeElement === trigger || trigger.contains(activeElement));

    if (!visible && focusInside) {
      this.moveFocusToTrigger();
    }

    this.classList.toggle('is-visible', visible);
    this.toggleAttribute('inert', !visible);

    if (visible) {
      this.removeAttribute('aria-hidden');
      if (focusOnTrigger) {
        requestAnimationFrame(() => this.moveFocusToPrimaryControl());
      }
    } else {
      this.setAttribute('aria-hidden', 'true');
    }
  }

  getPrimaryControl() {
    if (this.submitButton && !this.submitButton.hidden && !this.submitButton.disabled) {
      return this.submitButton;
    }

    const notifyHidden = this.notifyButton?.closest('[hidden]');
    if (this.notifyButton && !notifyHidden) {
      return this.notifyButton;
    }

    return this.querySelector('button:not([hidden]):not([disabled]), input:not([hidden]):not([disabled])');
  }

  moveFocusToPrimaryControl() {
    if (!this.classList.contains('is-visible') || this.hasAttribute('inert')) return;
    const control = this.getPrimaryControl();
    if (control && typeof control.focus === 'function') {
      control.focus();
    }
  }

  moveFocusToTrigger() {
    const trigger = this.currentTrigger;
    let control = null;

    if (trigger instanceof Element) {
      if (trigger.matches('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])')) {
        control = trigger;
      } else {
        control = trigger.querySelector(
          'button.product-form__atc-button:not([hidden]), button[name="add"]:not([hidden]), .section-main-product-notify-me, button, input, a'
        );
      }
    }

    if (!control) {
      control = document.querySelector(
        'product-info button.product-form__atc-button:not([hidden]), product-info .section-main-product-notify-me'
      );
    }

    if (control && typeof control.focus === 'function') {
      control.focus();
    }
  }

  onSectionLoad(event) {
    if (event.target?.contains?.(this) || event.target?.querySelector?.('product-info')) {
      this.observeTrigger();
      this.syncFromProduct();
    }
  }

  onVariantChange() {
    this.observeTrigger();
    this.syncFromProduct();
  }

  syncFromProduct() {
    const productInfo = this.productInfo();
    const variantId = productInfo?.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
    const variant = this.variants.find((item) => String(item.id) === String(variantId));

    if (this.variantInput && variantId) {
      this.variantInput.value = variantId;
    }

    const title = productInfo?.querySelector('.product__title h1')?.textContent?.trim();
    if (title && this.titleEl) {
      this.titleEl.textContent = title;
      this.updateNotifyLabel(title);
      this.updateRegionLabel(title);
    }

    if (!this.syncPriceFromProduct(productInfo) && variant) {
      this.updatePrice(variant);
    }

    if (!this.syncImageFromProduct(productInfo) && variant) {
      this.updateImage(variant);
    }

    if (!this.syncAvailabilityFromProduct(productInfo) && variant) {
      this.toggleAvailability(Boolean(variant.available));
    }

    this.syncQuantityRules(productInfo);
  }

  updateNotifyLabel(title) {
    if (!this.notifyButton || !title) return;
    const template = this.dataset.notifyLabel || '';
    if (template.includes('__TITLE__')) {
      this.notifyButton.setAttribute('aria-label', template.replaceAll('__TITLE__', title));
    }
    this.dataset.productTitle = title;
  }

  updateRegionLabel(title) {
    if (!title) return;
    const template = this.dataset.regionLabel || '';
    const label = template.includes('__TITLE__') ? template.replaceAll('__TITLE__', title) : title;
    if (this.headingEl) this.headingEl.textContent = label;
    this.setAttribute('aria-label', label);
  }

  syncPriceFromProduct(productInfo) {
    if (!productInfo || !this.priceCurrentEl) return false;
    const priceRoot = productInfo.querySelector('.product__price .price');
    if (!priceRoot) return false;

    const onSale = priceRoot.classList.contains('price--on-sale');
    const current = onSale
      ? priceRoot.querySelector('.price-item--sale')
      : priceRoot.querySelector('.price__regular .price-item--regular');
    if (!current) return false;

    this.priceCurrentEl.textContent = current.textContent.trim();
    this.setPriceLabels(onSale);

    if (this.priceCompareEl) {
      const compare = priceRoot.querySelector('.price__sale s.price-item--regular');
      if (onSale && compare) {
        this.priceCompareEl.textContent = compare.textContent.trim();
        this.priceCompareEl.hidden = false;
      } else {
        this.priceCompareEl.textContent = '';
        this.priceCompareEl.hidden = true;
      }
    }

    return true;
  }

  updatePrice(variant) {
    if (this.priceCurrentEl && variant.price) {
      this.priceCurrentEl.textContent = variant.price;
    }

    const onSale = Boolean(variant.compare_at_price);
    this.setPriceLabels(onSale);

    if (!this.priceCompareEl) return;
    if (onSale) {
      this.priceCompareEl.textContent = variant.compare_at_price;
      this.priceCompareEl.hidden = false;
    } else {
      this.priceCompareEl.textContent = '';
      this.priceCompareEl.hidden = true;
    }
  }

  setPriceLabels(onSale) {
    if (this.priceCurrentLabelEl) {
      this.priceCurrentLabelEl.textContent = onSale
        ? this.dataset.saleLabel || ''
        : this.dataset.regularLabel || '';
    }
    if (this.priceCompareLabelEl) {
      this.priceCompareLabelEl.hidden = !onSale;
    }
  }

  updateImage(variant) {
    if (!this.imageEl || !variant.image) return false;
    this.imageEl.src = variant.image;
    this.imageEl.removeAttribute('srcset');
    this.imageEl.alt = variant.image_alt || this.titleEl?.textContent || '';
    return true;
  }

  syncImageFromProduct(productInfo) {
    if (!this.imageEl || !productInfo) return false;
    const sourceImg =
      productInfo.querySelector('.product-media-gallery__main .swiper-slide-active img') ||
      productInfo.querySelector('.product-media-gallery img');
    if (!sourceImg) return false;
    this.imageEl.src = sourceImg.currentSrc || sourceImg.src;
    this.imageEl.removeAttribute('srcset');
    this.imageEl.alt = sourceImg.alt || this.titleEl?.textContent || '';
    return true;
  }

  syncAvailabilityFromProduct(productInfo) {
    const mainButton = productInfo?.querySelector('.product-form__atc-button');
    if (!mainButton) return false;
    const available = !mainButton.hidden && !mainButton.classList.contains('is-hidden');
    this.toggleAvailability(available);
    return true;
  }

  toggleAvailability(available) {
    if (this.submitButton) {
      this.submitButton.classList.toggle('is-hidden', !available);
      this.submitButton.toggleAttribute('hidden', !available);
      this.submitButton.disabled = !available;
    }

    if (this.bis) {
      this.bis.classList.toggle('is-hidden', available);
      this.bis.toggleAttribute('hidden', available);
    }

    if (this.notifyButton) {
      this.notifyButton.setAttribute('tabindex', available ? '-1' : '0');
    }
  }

  syncQuantityRules(productInfo) {
    if (!this.quantityInput || !productInfo) return;
    const mainQuantity = productInfo.querySelector('input[name="quantity"]');
    if (!mainQuantity) return;

    ['min', 'max', 'step'].forEach((attr) => {
      const value = mainQuantity.getAttribute(attr);
      if (value) {
        this.quantityInput.setAttribute(attr, value);
      } else {
        this.quantityInput.removeAttribute(attr);
      }
    });
  }

  clampQuantity(value) {
    const min = parseInt(this.quantityInput?.getAttribute('min'), 10) || 1;
    const maxAttr = this.quantityInput?.getAttribute('max');
    const max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
    const step = parseInt(this.quantityInput?.getAttribute('step'), 10) || 1;
    let next = Number.isFinite(value) ? value : min;
    if (next < min) next = min;
    if (next > max) next = max;
    return Math.round(next / step) * step;
  }

  setQuantity(value) {
    if (!this.quantityInput) return;
    const next = this.clampQuantity(value);
    this.quantityInput.value = String(next);

    const productInfo = this.productInfo();
    productInfo?.querySelectorAll('input[name="quantity"]').forEach((input) => {
      input.value = String(next);
    });
  }

  onQuantityClick(event) {
    const action = event.currentTarget?.getAttribute('name');
    const current = parseInt(this.quantityInput?.value, 10) || 1;
    const step = parseInt(this.quantityInput?.getAttribute('step'), 10) || 1;
    if (action === 'minus') {
      this.setQuantity(current - step);
    } else if (action === 'plus') {
      this.setQuantity(current + step);
    }
  }

  onQuantityInput() {
    this.setQuantity(parseInt(this.quantityInput?.value, 10));
  }

  onDocumentQuantityChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target === this.quantityInput) return;
    if (target.name !== 'quantity') return;
    if (!target.closest('product-info')) return;
    if (this.quantityInput) {
      this.quantityInput.value = target.value;
    }
  }

}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
